import { Suspense, lazy, useState } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom'
import type { ResearchSegment } from '@instrument'
import { Layout, PageSkeleton } from './components/Layout'
import { MarketingLanding } from './screens/MarketingLanding'

/**
 * The survey and the token-exchange screen are loaded on demand.
 *
 * `/` is a marketing page. Splitting here keeps the instrument, the flow graph,
 * every question screen, the charts, the zustand store and the API client out of
 * the landing page's critical path — none of it is needed to render a hero.
 */
const SurveyApp = lazy(() => import('./survey/SurveyApp'))
const EnterScreen = lazy(() => import('./screens/EnterScreen'))

/** `/q/:nodeId` predates the move to `/survey`. Carry the node id across. */
function LegacyQRedirect() {
	const { nodeId } = useParams()
	return <Navigate to={`/survey/q/${nodeId ?? ''}`} replace />
}

function Loading() {
	return (
		<Layout>
			<PageSkeleton />
		</Layout>
	)
}

/**
 * The route table.
 *
 * Two things here are load-bearing:
 *
 * 1. `<MarketingLanding>` is a *sibling* of the survey, never a child. The survey
 *    subtree opens a session — a POST and an HttpOnly credential cookie — the
 *    moment it mounts, and a visitor reading the hero must not become a research
 *    session row before any consent screen has been shown.
 *
 * 2. `/enter` stays at the root. apps/api mints that link
 *    (`${RESEARCH_LAB_WEB_URL}/enter?token=…`) and the mobile app opens whatever
 *    it is given, so moving it would require a coordinated API release.
 */
export default function App() {
	const [entered, setEntered] = useState<{ sessionId: string; segment: ResearchSegment } | null>(null)

	return (
		<BrowserRouter>
			<Suspense fallback={<Loading />}>
				<Routes>
					<Route path="/" element={<MarketingLanding />} />
					<Route
						path="/enter"
						element={<EnterScreen onReady={(sessionId, segment) => setEntered({ sessionId, segment })} />}
					/>
					<Route path="/q/:nodeId" element={<LegacyQRedirect />} />
					<Route path="/survey/*" element={<SurveyApp key={entered?.sessionId ?? 'anonymous'} />} />
					<Route path="*" element={<Navigate to="/" replace />} />
				</Routes>
			</Suspense>
		</BrowserRouter>
	)
}
